import {
  Controller,
  Control,
  FieldPath,
  RegisterOptions,
  FieldValues,
} from 'react-hook-form';
import {
  FormControl,
  Select,
  MenuItem,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { useId } from 'react';
import { OutlineChevronUpDown } from '~/assets/icons';
import { theme } from '~/shared/styles/theme';

interface SelectOption {
  value: string;
  label: string;
}

interface Props<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  placeholder: string;
  options: readonly SelectOption[];
  required?: boolean;
  rules?: RegisterOptions<T>;
  disabled?: boolean;
}

const FormSelect = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  options,
  required = false,
  rules,
  disabled = false,
}: Props<T>) => {
  const labelId = useId();

  const finalRules: RegisterOptions<T> = required
    ? { required: `${label} is required`, ...rules }
    : rules || {};

  return (
    <Stack width="100%">
      <Label
        id={labelId}
        variant="body2"
        color={theme.palette.custom.textTitle}
      >
        {label}
        {required && '*'}
      </Label>
      <FormControl fullWidth required={required}>
        <Controller
          name={name}
          control={control}
          rules={finalRules}
          disabled={disabled}
          render={({ field: { value, onChange, ref, ...field } }) => (
            <Select
              {...field}
              inputRef={ref}
              value={value ?? ''}
              onChange={(event) => {
                const selectedValue = event.target.value;
                // Transform empty strings to undefined so Zod validation works properly
                onChange(selectedValue === '' ? undefined : selectedValue);
              }}
              displayEmpty
              disabled={disabled}
              aria-labelledby={labelId}
              IconComponent={() => (
                <Stack pr={3}>
                  <OutlineChevronUpDown />
                </Stack>
              )}
            >
              <MenuItem value="" disabled>
                <Placeholder>{placeholder}</Placeholder>
              </MenuItem>
              {options.map(({ value: optionValue, label: optionLabel }) => (
                <MenuItem value={optionValue} key={optionValue}>
                  <OptionLabel>{optionLabel}</OptionLabel>
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </FormControl>
    </Stack>
  );
};

export default FormSelect;

const Label = styled(Typography)({
  marginBottom: theme.spacing(1),
});

const Placeholder = styled(Typography)({
  fontSize: 14,
  color: theme.palette.custom.textDescription,
});

const OptionLabel = styled(Typography)({
  fontSize: 14,
  color: theme.palette.custom.textDescription,
});
